const EMPTY_RESPONSE_STATUS_CODES = new Set([204, 205])

const createInterceptorManager = () => {
   const handlers = []

   return {
       use(fulfilled, rejected) {
           handlers.push({ fulfilled, rejected })
           return handlers.length - 1
       },
       eject(id) {
           if (handlers[id]) {
               handlers[id] = null
           }
       },
       forEach(callback) {
           handlers.forEach(handler => {
               if (handler) {
                   callback(handler)
               }
           })
       }
   }
}

const hasHeader = (headers, headerName) => {
   if (!headers) {
       return false
   }

   const targetHeader = headerName.toLowerCase()

   if (typeof Headers !== "undefined" && headers instanceof Headers) {
       return headers.has(headerName)
   }

   return Object.keys(headers).some(key => key.toLowerCase() === targetHeader)
}


const toRequestHeaders = headers => {
   if (!headers) {
       return {}
   }

   if (typeof Headers !== "undefined" && headers instanceof Headers) {
       return Object.fromEntries(headers.entries())
   }

   if (Array.isArray(headers)) {
       return Object.fromEntries(headers)
   }

   return { ...headers }
}

const isStreamBody = value =>
   typeof ReadableStream !== "undefined" && value instanceof ReadableStream


const isBinaryBody = value =>
   typeof ArrayBuffer !== "undefined" &&
   (value instanceof ArrayBuffer || ArrayBuffer.isView(value))


const isNativeBody = value =>
   typeof value === "string" ||
   (typeof FormData !== "undefined" && value instanceof FormData) ||
   (typeof Blob !== "undefined" && value instanceof Blob) ||
   (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) ||
   isBinaryBody(value) ||
   isStreamBody(value)


const serializeRequestBody = (data, headers) => {
   if (data == null) {
       return undefined
   }

   if (isNativeBody(data)) {
       return data
   }

   if (!hasHeader(headers, "Content-Type")) {
       headers["Content-Type"] = "application/json"
   }

   return JSON.stringify(data)
}


const shouldTryParseJson = (contentType, responseText) => {
   if (!responseText) {
       return false
   }

   if (contentType.includes("application/json") || contentType.includes("+json")) {
       return true
   }

   const trimmedText = responseText.trim()

   if (!trimmedText) {
       return false
   }

   return (
       ["{", "[", "\""].includes(trimmedText[0]) ||
       trimmedText === "null" ||
       trimmedText === "true" ||
       trimmedText === "false" ||
       /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmedText)
   )
}

const parseResponseData = async response => {
   if (EMPTY_RESPONSE_STATUS_CODES.has(response.status)) {
       return null
   }

   const responseText = await response.text()

   if (!responseText) {
       return null
   }

   const contentType = response.headers?.get?.("content-type") ?? ""

   if (shouldTryParseJson(contentType, responseText)) {
       try {
           return JSON.parse(responseText)
       }
       catch (error) {
           if (error instanceof SyntaxError) {
               return responseText
           }
           throw error
       }
   }

   return responseText
}

const formatResponseData = data => {
    if (data == null) return null
    if (typeof data === "string") return data
    if (typeof data === "object") {
        return Object.values(data).flat().join(". ")
    }
    return String(data)
}

const createRequestConfig = (method, url, data, config = {}) => ({
   ...config,
   method,
   url,
   data,
   headers: toRequestHeaders(config.headers)
})

const createResponse = async (nativeResponse, config) => {
   const data = await parseResponseData(nativeResponse)

   return {
       data,
       status: nativeResponse.status,
       statusText: nativeResponse.statusText,
       headers: nativeResponse.headers,
       config
   }
}

const createHttpError = response => {
    const responseData = response.data == null ? response.statusText : formatResponseData(response.data)
    const error = new Error(`${responseData} (${response.status})`)
    error.config = response.config
    error.request = {
        method: response.config.method,
        url: response.config.url
    }
    error.response = response

    return error
}

const createNetworkError = (error, config) => {
   const networkError = new Error(error?.message ?? "Network Error")
   networkError.config = config
   networkError.request = {
       method: config.method,
       url: config.url
   }

   if (error?.name === "AbortError") {
       networkError.code = "ERR_CANCELED"
   }

   return networkError
}

const dispatchRequest = async config => {
   const headers = toRequestHeaders(config.headers)
   const requestInit = {
       method: config.method,
       headers,
       signal: config.signal,
       credentials: config.credentials,
       cache: config.cache,
       mode: config.mode,
       redirect: config.redirect,
       referrer: config.referrer,
       referrerPolicy: config.referrerPolicy,
       integrity: config.integrity,
       keepalive: config.keepalive
   }

   const requestBody = serializeRequestBody(config.data, headers)

   if (requestBody !== undefined) {
       requestInit.body = requestBody
   }

   let nativeResponse

   try {
       nativeResponse = await fetch(config.url, requestInit)
   }
   catch (error) {
       throw createNetworkError(error, { ...config, headers })
   }

   const response = await createResponse(nativeResponse, { ...config, headers })

   if (!nativeResponse.ok) {
       throw createHttpError(response)
   }

   return response
}

const apiFetch = {
   interceptors: {
       request: createInterceptorManager(),
       response: createInterceptorManager()
   },
   request(config) {
       let promise = Promise.resolve(config)

       this.interceptors.request.forEach(({ fulfilled, rejected }) => {
           promise = promise.then(fulfilled, rejected)
       })

       promise = promise.then(dispatchRequest)

       this.interceptors.response.forEach(({ fulfilled, rejected }) => {
           promise = promise.then(fulfilled, rejected)
       })

       return promise
   },
   get(url, config) {
       return this.request(createRequestConfig("GET", url, undefined, config))
   },
   delete(url, config) {
       return this.request(createRequestConfig("DELETE", url, undefined, config))
   },
   post(url, data, config) {
       return this.request(createRequestConfig("POST", url, data, config))
   },
   put(url, data, config) {
       return this.request(createRequestConfig("PUT", url, data, config))
   }
}

export default apiFetch